import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const STATUS = { open: "Abierta", assigned: "Asignada", escalated: "Escalada", closed: "Cerrada", resolved: "Resuelta", abandoned: "Abandonada" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-DO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function ConvRow({ c }) {
  return (
    <Link to={`/inbox?conv=${c.id}`} className="block px-3 py-2 hover:bg-secondary/50 transition-colors" data-testid={`agent-conv-${c.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-semibold truncate">{c.customer_name}</span>
        <Badge variant="outline" className="text-[9px] uppercase">{c.channel}</Badge>
        <span className="text-muted-foreground">{STATUS[c.status] || c.status}</span>
        {c.csat != null && <span className="ml-auto text-amber-500">★ {c.csat}</span>}
      </div>
      <div className="text-[11px] text-muted-foreground truncate">{c.last_message_preview || "—"}</div>
      <div className="text-[10px] text-muted-foreground">{fmt(c.created_at)}{c.handle_minutes != null ? ` · AHT ${c.handle_minutes} min` : ""}</div>
    </Link>
  );
}

export const AgentDetailSheet = ({ agentId, dateFrom, dateTo, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!agentId) return;
    setData(null);
    api.get(`/analytics/agent/${agentId}?from=${dateFrom}&to=${dateTo}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [agentId, dateFrom, dateTo]);

  return (
    <Sheet open={!!agentId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="agent-detail-sheet">
        <SheetHeader>
          <SheetTitle data-testid="agent-detail-name">{data?.agent?.name || "Asesor"}</SheetTitle>
          <SheetDescription>{dateFrom} → {dateTo}{data?.agent?.specialties?.length ? ` · ${data.agent.specialties.join(", ")}` : ""}</SheetDescription>
        </SheetHeader>
        {!data && <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>}
        {data?.error && <div className="py-10 text-center text-sm text-rose-600">No se pudo cargar el detalle</div>}
        {data?.agent && (
          <div className="space-y-5 mt-4">
            <section>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Tendencia diaria</div>
              {data.daily.length ? (
                <div className="h-44" data-testid="agent-detail-trend">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.daily}>
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 9 }} width={24} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Line type="monotone" dataKey="attended" name="Atendidas" stroke="#0A1420" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="closed" name="Cerradas" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <div className="text-xs text-muted-foreground">Sin actividad en el rango.</div>}
            </section>
            <section>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Comentarios CSAT <span className="font-normal">({data.csat_comments.length} de {data.csat_count} encuestas)</span>
              </div>
              <div className="space-y-2" data-testid="agent-detail-csat">
                {data.csat_comments.length === 0 && <div className="text-xs text-muted-foreground">Sin comentarios en el rango.</div>}
                {data.csat_comments.map((r, i) => (
                  <div key={i} className="border border-border rounded-md p-2 text-xs">
                    <div className="flex items-center gap-2"><span className="text-amber-500">{"★".repeat(r.score)}</span><span className="text-muted-foreground">{fmt(r.created_at)}</span></div>
                    <div className="mt-1">“{r.comment}”</div>
                  </div>
                ))}
              </div>
            </section>
            <section>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Conversaciones ({data.conversations.length})</div>
              <div className="border border-border rounded-md divide-y divide-border" data-testid="agent-detail-convs">
                {data.conversations.length === 0 && <div className="px-3 py-4 text-xs text-muted-foreground">Sin conversaciones en el rango.</div>}
                {data.conversations.map((c) => <ConvRow key={c.id} c={c} />)}
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AgentDetailSheet;
